import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Home, Search, AlertCircle } from "lucide-react";

const NotFound = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-green-50 relative overflow-hidden flex items-center justify-center px-4">
      {/* Healthcare Pattern Background */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-10 left-10 w-6 h-6 text-blue-300">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
          </svg>
        </div>
        <div className="absolute top-20 right-20 w-8 h-8 text-green-300">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14h-4v-4H6v-2h4V7h4v4h4v2h-4v4z" />
          </svg>
        </div>
        <div className="absolute bottom-20 left-20 w-7 h-7 text-teal-300">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
          </svg>
        </div>
      </div>

      <div className="relative z-10 max-w-3xl mx-auto text-center">
        {/* Medical Cross and 404 */}
        <div className="mb-12 relative">
          <div className="relative inline-block">
            {/* Medical Cross */}
            <div className="w-32 h-32 mx-auto mb-8 relative animate-gentle-pulse">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-teal-500 rounded-2xl shadow-2xl transform rotate-45">
                <div className="absolute inset-4 bg-white rounded-lg"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-16 h-4 bg-gradient-to-r from-blue-500 to-teal-500 rounded-full"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-4 h-16 bg-gradient-to-b from-blue-500 to-teal-500 rounded-full"></div>
              </div>
            </div>
            {/* Floating Medical Icons */}
            <div className="absolute -top-4 -left-8 animate-float-gentle">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center shadow-lg">
                <Search className="w-4 h-4 text-blue-600" />
              </div>
            </div>
            <div className="absolute -top-2 -right-8 animate-float-gentle-delay">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center shadow-lg">
                <AlertCircle className="w-4 h-4 text-green-600" />
              </div>
            </div>
          </div>
          {/* 404 Text */}
          <div className="mt-8">
            <h1 className="text-8xl font-bold text-gray-800 mb-4">
              <span className="text-blue-600">4</span>
              <span className="text-teal-600">0</span>
              <span className="text-green-600">4</span>
            </h1>
          </div>
        </div>

        {/* Message */}
        <div className="mb-12 space-y-6">
          <h2 className="text-4xl font-bold text-gray-800 mb-4">
            Page Not Found
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto">
            We couldn't locate the page you're looking for. It may have been moved or is temporarily unavailable.
          </p>
        </div>

        {/* Action Button */}
        <div className="mb-8">
          <Link to="/">
            <Button className="bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 text-white px-12 py-4 rounded-full text-lg font-semibold shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
              <Home className="w-6 h-6 mr-3" />
              Return to Dashboard
            </Button>
          </Link>
        </div>
      </div>

      {/* Floating Medical Elements */}
      <div className="absolute top-1/4 left-10 animate-float-slow opacity-20">
        <div className="w-16 h-16 bg-blue-200 rounded-full flex items-center justify-center">
          <div className="w-8 h-2 bg-blue-500 rounded-full"></div>
          <div className="absolute w-2 h-8 bg-blue-500 rounded-full"></div>
        </div>
      </div>
      <div className="absolute top-1/3 right-16 animate-float-slow-delay opacity-20">
        <div className="w-12 h-12 bg-green-200 rounded-full flex items-center justify-center">
          <div className="w-6 h-1.5 bg-green-500 rounded-full"></div>
          <div className="absolute w-1.5 h-6 bg-green-500 rounded-full"></div>
        </div>
      </div>

      <div className="absolute bottom-1/4 left-16 animate-float-gentle opacity-20">
        <div className="w-14 h-14 bg-teal-200 rounded-full flex items-center justify-center">
          <div className="w-7 h-1.5 bg-teal-500 rounded-full"></div>
          <div className="absolute w-1.5 h-7 bg-teal-500 rounded-full"></div>
        </div>
      </div>

      {/* Clean Healthcare Animations */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes gentle-pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }
          
          @keyframes float-gentle {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-8px); }
          }
          
          @keyframes float-gentle-delay {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-6px); }
          }
          
          @keyframes float-slow {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            50% { transform: translateY(-12px) rotate(2deg); }
          }
          
          @keyframes float-slow-delay {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            50% { transform: translateY(-10px) rotate(-1deg); }
          }
          
          .animate-gentle-pulse { animation: gentle-pulse 3s ease-in-out infinite; }
          .animate-float-gentle { animation: float-gentle 4s ease-in-out infinite; }
          .animate-float-gentle-delay { animation: float-gentle-delay 4s ease-in-out infinite 0.5s; }
          .animate-float-slow { animation: float-slow 6s ease-in-out infinite; }
          .animate-float-slow-delay { animation: float-slow-delay 6s ease-in-out infinite 1s; }
        `
      }} />
    </div>
  );
};

export default NotFound;